/**
 * The plan, as a coloured chip.
 *
 * Wherever the app names a plan — this prompt, the account menu, the billing
 * panel, the explanation on a locked control — it should look the same. Colour
 * then does work that a repeated sentence otherwise has to: you learn once that
 * violet means Studio, and every later mention is free.
 *
 * All three identities live in design tokens (--tier-*-tint / -wash / -edge),
 * so retuning a plan's colour is one edit to design-tokens.json rather than a
 * search for a hex across the codebase.
 */
import { TIER_ICONS, TIER_NAMES, type Tier } from "../../shared/tiers";
import { el } from "./dom";

export interface TierChipOptions {
  /**
   * Solid fill rather than a tinted outline.
   *
   * For the one place on a screen where the plan is the subject — a prompt's
   * heading, the current plan in billing. Everywhere it is merely referenced,
   * the default is quieter and does not compete with the sentence around it.
   */
  solid?: boolean;
  /** Overrides the plan's own name, for cases like "Creator and up". */
  label?: string;
}

export function tierChip(tier: Tier, options: TierChipOptions = {}): HTMLElement {
  const chip = el("span", {
    class: options.solid ? "tier-chip tier-chip--solid" : "tier-chip",
    data: { tier },
  });

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", "icon");
  // Decorative: the label beside it already says the plan, and a screen reader
  // announcing "palette Creator" is worse than one announcing "Creator".
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
  use.setAttribute("href", `#${TIER_ICONS[tier]}`);
  svg.appendChild(use);

  chip.appendChild(svg);
  chip.appendChild(el("span", { text: options.label ?? TIER_NAMES[tier] }));
  return chip;
}
